import { useTranslation } from 'react-i18next';
/**
 * Live type preview for the Edit Type dialog — a bottom strip spanning the
 * full dialog width. Renders the draft type parameters as an SVG figure: a
 * plan cut for layered assemblies, an elevation for openings, or a simple
 * mass for everything else.
 *
 * Layout contract (Apple HIG): the figure lives in a fixed-height stage with
 * a uniform viewBox, so it scales proportionally and letterboxes in the
 * center at any window width — it can never distort, overflow, or push the
 * dialog taller, and the reserved height prevents layout shift.
 */

import type { ReactElement } from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';

interface TypePreviewPanelProps {
    category: string;
    parameters: Record<string, any>;
}

/** Shared drawing space for every figure; keep all geometry inside margins. */
const VB_W = 260;
const VB_H = 195;
const MARGIN = 18;

/** Material band colors (data palette for the cut, not UI chrome). */
const LAYER_COLORS: Record<string, string> = {
    Structure: '#8C7A5E',
    Substrate: '#A8997D',
    'Thermal/Air': '#5B8DB8',
    'Finish 1': '#C7BBA4',
    'Finish 2': '#C7BBA4',
    Membrane: '#4CAF50',
    'Membrane 2': '#81C784',
    Wrapping: '#C7BBA4',
};
const BAND_FALLBACK = '#6E6A64';

/** Theme-driven text paints so the preview follows light/dark themes. */
const labelStyle = { fill: 'var(--color-text-secondary)' } as const;
const dimStyle = { fill: 'var(--color-text-muted)' } as const;
const lineStroke = 'var(--color-border-light)';

function mm(value: any): number {
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(n) ? Math.round(n * 1000) : 0;
}

function resolveLayers(parameters: Record<string, any>): { function: string; thicknessMm: number }[] {
    const raw = parameters['layers'] ?? parameters['Layers'];
    const list = Array.isArray(raw) && raw.length > 0
        ? raw
        : [{ function: 'Structure', thickness: typeof parameters['thickness'] === 'number' ? parameters['thickness'] : 0.2 }];
    return list.map((l: any) => ({
        function: String(l.function ?? 'Structure'),
        thicknessMm: Math.max(mm(l.thickness), 0),
    }));
}

/** Layered plan cut (walls / floors / roofs / ceilings / structural). */
function PlanCutFigure({ parameters }: { parameters: Record<string, any> }) {
    const { t } = useTranslation();
    const layers = resolveLayers(parameters);
    const totalMm = layers.reduce((sum, l) => sum + l.thicknessMm, 0) || 200;

    const bandX = MARGIN + 10;
    const bandMaxW = VB_W - bandX - 58;
    const bandMaxH = 108;
    const scale = Math.min(bandMaxH / totalMm, 0.6);
    const bandH = Math.max(totalMm * scale, 4);
    const top = (VB_H - bandH) / 2 - 8;

    // Prefix-sum layout before rendering (no mutation inside JSX).
    let cursor = top;
    const rects = layers.map((layer) => {
        const { t } = useTranslation();
        const h = Math.max(layer.thicknessMm * scale, 1);
        const rect = { ...layer, y: cursor, h };
        cursor += h;
        return rect;
    });

    return (
        <g>
            {rects.map((l, i) => (
                <g key={i}>
                    <rect
                        x={bandX}
                        y={l.y}
                        width={bandMaxW}
                        height={l.h}
                        style={{ fill: LAYER_COLORS[l.function] ?? BAND_FALLBACK }}
                        stroke="var(--color-bg-primary)"
                        strokeWidth="0.6"
                    />
                    {l.h >= 8 && (
                        <text x={VB_W - MARGIN - 34} y={l.y + l.h / 2 + 3} fontSize="8" style={labelStyle} textAnchor="end">
                            {l.thicknessMm}
                        </text>
                    )}
                </g>
            ))}
            {/* Face annotations */}
            <text x={bandX} y={top - 6} fontSize="8" style={dimStyle} textAnchor="start">{t('misc.exteriorFinish1')}</text>
            <text x={bandX} y={top + bandH + 12} fontSize="8" style={dimStyle} textAnchor="start">{t('misc.interiorFinish2')}</text>
            {/* Overall thickness dimension line on the right */}
            <line x1={VB_W - MARGIN - 24} y1={top} x2={VB_W - MARGIN - 24} y2={top + bandH} stroke={lineStroke} strokeWidth="0.8" />
            <line x1={VB_W - MARGIN - 28} y1={top} x2={VB_W - MARGIN - 20} y2={top} stroke={lineStroke} strokeWidth="0.8" />
            <line x1={VB_W - MARGIN - 28} y1={top + bandH} x2={VB_W - MARGIN - 20} y2={top + bandH} stroke={lineStroke} strokeWidth="0.8" />
            <text x={VB_W - MARGIN - 14} y={top + bandH / 2 + 3} fontSize="8" style={labelStyle} textAnchor="start">
                {Math.round(rects.reduce((s, r) => s + r.thicknessMm, 0))} mm
            </text>
        </g>
    );
}

/** Door / window elevation with width and height dimensions. */
function OpeningFigure({ parameters, kind }: { parameters: Record<string, any>; kind: 'door' | 'window' }) {
    const wMm = mm(parameters['Width'] ?? parameters['width']) || 900;
    const hMm = mm(parameters['Height'] ?? parameters['height']) || (kind === 'door' ? 2100 : 1500);
    const frameMm = mm(parameters['FrameWidth'] ?? parameters['Frame Width']) || 50;

    const maxW = VB_W - MARGIN * 2 - 30;
    const maxH = VB_H - MARGIN * 2 - 26;
    const scale = Math.min(maxW / wMm, maxH / hMm);
    const bw = wMm * scale;
    const bh = hMm * scale;
    const x = (VB_W - bw) / 2 + 8;
    const y = (VB_H - bh) / 2 - 6;
    const inset = Math.max(Math.min(frameMm * scale, bw / 6, bh / 6), 1.5);
    const fill = kind === 'window' ? '#9FC6DE' : '#6E6152';

    return (
        <g>
            <rect x={x} y={y} width={bw} height={bh} style={{ fill }} stroke="var(--color-bg-primary)" strokeWidth="0.8" />
            <rect x={x + inset} y={y + inset} width={Math.max(bw - inset * 2, 1)} height={Math.max(bh - inset * 2, 1)} fill="none" stroke="var(--color-bg-primary)" strokeWidth="0.6" opacity="0.7" />
            {/* Door handle hint */}
            {kind === 'door' && (
                <line x1={x + bw * 0.5} y1={y + bh * 0.46} x2={x + bw * 0.5} y2={y + bh * 0.54} stroke="var(--color-bg-primary)" strokeWidth="1.4" />
            )}
            {/* Width dimension under the opening */}
            <line x1={x} y1={y + bh + 12} x2={x + bw} y2={y + bh + 12} stroke={lineStroke} strokeWidth="0.8" />
            <line x1={x} y1={y + bh + 8} x2={x} y2={y + bh + 16} stroke={lineStroke} strokeWidth="0.8" />
            <line x1={x + bw} y1={y + bh + 8} x2={x + bw} y2={y + bh + 16} stroke={lineStroke} strokeWidth="0.8" />
            <text x={VB_W / 2 + 4} y={y + bh + 26} fontSize="8" style={labelStyle} textAnchor="middle">{wMm} × {hMm} mm</text>
        </g>
    );
}

/** Isometric mass fallback for categories without a dedicated figure. */
function MassFigure({ parameters }: { parameters: Record<string, any> }) {
    const wMm = mm(parameters['Width'] ?? parameters['width'] ?? parameters['Depth']) || 600;
    const hMm = mm(parameters['Height'] ?? parameters['height'] ?? parameters['thickness'] ?? parameters['Thickness']) || 600;
    const dMm = mm(parameters['Depth'] ?? parameters['thickness']) || Math.min(wMm, 300);

    const maxSpan = Math.max(wMm, hMm, dMm);
    const scale = 62 / maxSpan;
    const w = wMm * scale;
    const h = hMm * scale;
    const d = dMm * scale;
    const cx = VB_W / 2 - w / 2;
    const cy = VB_H / 2 - h / 2 + 4;

    return (
        <g>
            <polygon points={`${cx},${cy} ${cx + d * 0.5},${cy - d * 0.35} ${cx + w + d * 0.5},${cy - d * 0.35} ${cx + w},${cy}`} fill="#7D8CA3" stroke="var(--color-bg-primary)" strokeWidth="0.8" />
            <polygon points={`${cx + w},${cy} ${cx + w + d * 0.5},${cy - d * 0.35} ${cx + w + d * 0.5},${cy - d * 0.35 + h} ${cx + w},${cy + h}`} fill="#5C6B80" stroke="var(--color-bg-primary)" strokeWidth="0.8" />
            <rect x={cx} y={cy} width={w} height={h} fill="#6F7F96" stroke="var(--color-bg-primary)" strokeWidth="0.8" />
            <text x={VB_W / 2} y={cy + h + d * 0.6 + 14} fontSize="8" style={dimStyle} textAnchor="middle">
                {wMm} × {dMm} × {hMm} mm
            </text>
        </g>
    );
}

const LAYERED_CATEGORIES = new Set(['Walls', 'Floors', 'Roofs', 'Ceilings', 'Structural Framing', 'Structural Columns']);

export function TypePreviewPanel({ category, parameters }: TypePreviewPanelProps) {
    const { t } = useTranslation();
    let figure: ReactElement;
    let caption: string;

    if (category === 'Doors') {
        figure = <OpeningFigure parameters={parameters} kind="door" />;
        caption = 'Elevation';
    } else if (category === 'Windows') {
        figure = <OpeningFigure parameters={parameters} kind="window" />;
        caption = 'Elevation';
    } else if (LAYERED_CATEGORIES.has(category)) {
        figure = <PlanCutFigure parameters={parameters} />;
        caption = 'Plan / Cut';
    } else {
        figure = <MassFigure parameters={parameters} />;
        caption = 'Mass';
    }

    return (
        <VStack
            spacing={0}
            align="stretch"
            bg="var(--color-bg-tertiary)"
            border="1px solid var(--color-border)"
            borderRadius="var(--cad-radius-lg)"
            overflow="hidden"
        >
            {/* Fixed-height stage: the SVG letterboxes inside it, so the
                figure can never clip or stretch the dialog column. */}
            <Box w="full" h={{ base: '140px', xl: '160px' }}>
                <svg
                    viewBox={`0 0 ${VB_W} ${VB_H}`}
                    width="100%"
                    height="100%"
                    preserveAspectRatio="xMidYMid meet"
                    className="svg-block"
                    role="img"
                    aria-label={`${caption} preview`}
                >
                    <title>{`${caption} preview`}</title>
                    {figure}
                </svg>
            </Box>
            <Box pb={2} px={3}>
                <Text fontSize="xs" color="var(--color-text-secondary)" textAlign="center">
                    {caption}
                </Text>
            </Box>
        </VStack>
    );
}
